/**
 * Live progress panel shown while a preprocess batch is running (§5.1.5).
 *
 * Reads from `GET /preprocess/start`'s progress endpoint which returns:
 *   {
 *     running, done, error,
 *     current_episode, current_stage,
 *     total_episodes, episodes_done,
 *     stages: [{episode, stage, ok, duration, error}]
 *   }
 *
 * Displays:
 *   - Big "Episode X of N — stage_name" header with ETA
 *   - Overall progress bar (stagesDone / expectedStages)
 *   - Per-episode mini-status grid (queued / in-progress / done / failed)
 *   - Collapsible recent-activity log
 */
import { useState } from 'react'
import {
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
  ChevronDown,
  ChevronRight,
  Sparkles,
} from 'lucide-react'

import { Progress } from '@/components/ui/progress'
import type { DramaLang, PreprocessProgress } from '@/types/drama'
import { dramaT } from './i18n'

interface Props {
  progress: PreprocessProgress
  totalEpisodes: number  // fallback when progress.total_episodes missing
  lang: DramaLang
}

const PER_EPISODE_STAGES = 9
// Project-wide stages (see _PROJECT_STAGE_NAMES in handler/drama_handler.py):
//   1. story_outline_generation
//   2. cast_finalization
//   3. segment_character_attribution
//   4. character_avatar_match
const PROJECT_STAGES = 4

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds.toFixed(0)}s`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`
  return `${(seconds / 3600).toFixed(1)}h`
}

function formatEta(seconds: number): string {
  if (seconds < 60) return `~${Math.max(1, Math.round(seconds))}s`
  if (seconds < 3600) return `~${Math.round(seconds / 60)} min`
  return `~${(seconds / 3600).toFixed(1)} h`
}

export function PreprocessProgressPanel({ progress, totalEpisodes, lang }: Props) {
  const t = dramaT(lang)
  const [logOpen, setLogOpen] = useState(false)
  // [REFACTOR-2026-06-08] Collapse the detail once analysis is done (null = use
  // the default: expanded while running, collapsed when complete).
  const [detailOpen, setDetailOpen] = useState<boolean | null>(null)

  const batchTotal = progress.total_episodes ?? totalEpisodes
  const expectedStages = batchTotal * PER_EPISODE_STAGES + PROJECT_STAGES

  // Aggregate stage results. The flat `stages` list is the source of truth for
  // durations and the activity log; the optional per-episode dict gives us
  // explicit status (running vs queued) which is hard to infer when multiple
  // episodes run in parallel.
  type EpAgg = { done: number; failed: boolean; durations: number[]; running: boolean }
  const perEp = new Map<number, EpAgg>()
  const projectAgg: EpAgg = { done: 0, failed: false, durations: [], running: false }
  for (const s of progress.stages ?? []) {
    const bucket =
      s.episode == null
        ? projectAgg
        : (perEp.get(s.episode) ??
            perEp.set(s.episode, { done: 0, failed: false, durations: [], running: false }).get(s.episode)!)
    if (s.ok === false) bucket.failed = true
    else {
      bucket.done++
      if (s.duration != null) bucket.durations.push(s.duration)
    }
  }
  // Merge in explicit per-episode state (parallel mode): preferred over the
  // heuristic-from-stages derivation above for status fidelity.
  if (progress.episodes) {
    for (const [k, v] of Object.entries(progress.episodes)) {
      const n = Number(k)
      if (Number.isNaN(n)) continue
      const bucket =
        perEp.get(n) ??
        perEp.set(n, { done: 0, failed: false, durations: [], running: false }).get(n)!
      if (v.status === 'processing') bucket.running = true
      if (v.status === 'failed') bucket.failed = true
      // Trust the explicit stages_done over the count derived from flat log.
      if (v.stages_done > bucket.done) bucket.done = v.stages_done
    }
  }
  const runningCount = Array.from(perEp.values()).filter((a) => a.running).length

  const allOkDurations: number[] = (progress.stages ?? [])
    .filter((s) => s.ok !== false && s.duration != null)
    .map((s) => s.duration!)
  // Successful stages drive the progress bar (failed stages don't move us forward).
  const successCount = (progress.stages ?? []).filter((s) => s.ok !== false).length
  const percent = Math.min(100, (successCount / Math.max(expectedStages, 1)) * 100)
  const remainingStages = Math.max(0, expectedStages - successCount)
  const avgDuration =
    allOkDurations.length >= 3
      ? allOkDurations.reduce((a, b) => a + b, 0) / allOkDurations.length
      : 0
  const eta = avgDuration > 0 ? remainingStages * avgDuration : null

  const isComplete = progress.done && !progress.error
  // Expanded while running; collapsed once complete — until the user overrides.
  const open = detailOpen ?? !isComplete
  const headlineColor = progress.error
    ? 'text-destructive'
    : isComplete
    ? 'text-emerald-700 dark:text-emerald-300'
    : 'text-amber-700 dark:text-amber-300'

  return (
    <div className="p-4 border rounded-lg bg-amber-50/60 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900 space-y-3">
      {/* Headline */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <button
            type="button"
            onClick={() => setDetailOpen(!open)}
            className={`flex items-center gap-2 font-semibold ${headlineColor}`}
            title={open ? t('prog.collapse') : t('prog.expand')}
          >
            {open ? <ChevronDown className="w-3.5 h-3.5 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 shrink-0" />}
            {isComplete ? (
              <CheckCircle2 className="w-4 h-4" />
            ) : progress.error ? (
              <XCircle className="w-4 h-4" />
            ) : (
              <Loader2 className="w-4 h-4 animate-spin" />
            )}
            {isComplete ? (
              t('prog.done')
            ) : runningCount > 1 ? (
              <>
                {t('prog.parallel', runningCount)}
                {progress.episodes_done != null && t('prog.doneOfTotal', progress.episodes_done, batchTotal)}
              </>
            ) : runningCount === 1 ? (
              // Find that single episode and its current stage.
              (() => {
                const epEntry = progress.episodes
                  ? Object.entries(progress.episodes).find(([, v]) => v.status === 'processing')
                  : null
                if (epEntry) {
                  const [ep, st] = epEntry
                  return (
                    <>
                      {t('prog.epStage', ep, batchTotal, st.current_stage || 'starting')}
                    </>
                  )
                }
                return <>{t('prog.analyzingFirst', batchTotal)}</>
              })()
            ) : progress.current_stage && progress.current_stage !== 'queued' && progress.current_stage !== 'ready' ? (
              <>{t('prog.finishing', progress.current_stage)}</>
            ) : (
              t('prog.queued')
            )}
          </button>
          {progress.error && (
            <div className="text-xs text-destructive mt-1 break-all">{progress.error}</div>
          )}
        </div>
        <div className="text-xs text-muted-foreground shrink-0 text-right">
          <div className="font-mono">
            {t('prog.stagesOf', successCount, expectedStages)}
          </div>
          {eta != null && !isComplete && !progress.error && (
            <div>{t('prog.remaining', formatEta(eta))}</div>
          )}
        </div>
      </div>

      {open && (<>
      <Progress value={percent} className="h-2.5" />

      {/* Per-episode breakdown */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2">
        {Array.from({ length: batchTotal }, (_, i) => i + 1).map((epNum) => {
          const agg = perEp.get(epNum) ?? { done: 0, failed: false, durations: [], running: false }
          const explicit = progress.episodes?.[String(epNum)] ?? progress.episodes?.[epNum]
          const isDone = agg.done >= PER_EPISODE_STAGES && !agg.failed
          const isFailed = agg.failed
          const isRunning = agg.running || (agg.done > 0 && !isDone && !progress.done)
          const totalDur = agg.durations.reduce((a, b) => a + b, 0)
          let icon = <Clock className="w-3.5 h-3.5 text-muted-foreground" />
          let label = t('prog.epQueued')
          let cls = 'text-muted-foreground'
          if (isFailed) {
            icon = <XCircle className="w-3.5 h-3.5 text-destructive" />
            label = t('prog.epFailedAt', agg.done + 1, PER_EPISODE_STAGES)
            cls = 'text-destructive'
          } else if (isDone) {
            icon = <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
            label = t('prog.epDone', totalDur > 0 ? formatDuration(totalDur) : '')
            cls = 'text-emerald-700 dark:text-emerald-300'
          } else if (isRunning) {
            icon = <Loader2 className="w-3.5 h-3.5 animate-spin text-amber-600" />
            const stageHint = explicit?.current_stage ? ` · ${explicit.current_stage}` : ''
            label = t('prog.epRunning', agg.done, PER_EPISODE_STAGES, stageHint)
            cls = 'text-amber-700 dark:text-amber-300'
          }
          return (
            <div
              key={epNum}
              className="flex items-center gap-2 px-2.5 py-1.5 rounded border bg-background/60 text-xs"
            >
              {icon}
              <span className="font-mono font-semibold">EP {epNum}</span>
              <span className={`${cls} truncate`}>{label}</span>
            </div>
          )
        })}
        {/* Project-wide stages row */}
        <div className="flex items-center gap-2 px-2.5 py-1.5 rounded border bg-background/60 text-xs col-span-full md:col-span-1">
          {projectAgg.failed ? (
            <XCircle className="w-3.5 h-3.5 text-destructive" />
          ) : projectAgg.done >= PROJECT_STAGES ? (
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
          ) : projectAgg.done > 0 || (progress.current_episode == null && progress.running) ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin text-amber-600" />
          ) : (
            <Clock className="w-3.5 h-3.5 text-muted-foreground" />
          )}
          <Sparkles className="w-3.5 h-3.5 text-spectrum-magenta" />
          <span className="font-mono font-semibold">FINAL</span>
          <span className="text-muted-foreground truncate">
            {t('prog.finalStages', projectAgg.done, PROJECT_STAGES)}
          </span>
        </div>
      </div>

      {/* Recent activity log (collapsed by default to save vertical space) */}
      {(progress.stages?.length ?? 0) > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setLogOpen((v) => !v)}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            {logOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            {t('prog.activityLog', progress.stages.length)}
          </button>
          {logOpen && (
            <div className="mt-2 max-h-40 overflow-y-auto rounded border bg-background/60 p-2 text-xs space-y-0.5 font-mono">
              {[...progress.stages].reverse().map((s, i) => (
                <div
                  key={`${s.episode}-${s.stage}-${i}`}
                  className={s.ok === false ? 'text-destructive' : 'text-muted-foreground'}
                >
                  <span>{s.ok === false ? '✗' : '✓'}</span>{' '}
                  <span>{s.episode != null ? `ep${s.episode}` : 'project'}</span>{' '}
                  <span className="text-foreground">{s.stage}</span>
                  {s.duration != null && <span> ({s.duration.toFixed(1)}s)</span>}
                  {s.error && <span className="ml-2">— {s.error}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      </>)}
    </div>
  )
}
